import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { CalendarDays, MapPin, Sparkles, Radio, Ticket } from "lucide-react";
import EventHighlights from "../components/EventHighlights";
import Countdown from "../components/Countdown";
import EventOrganizer from "../components/EventOrganizer";
import BuyTicketsModal from "../components/BuyTicketsModal";

const iconFor = (title = "") => {
  const t = title.toLowerCase();
  if (t.includes("telethon") || t.includes("marathon") || t.includes("stream")) return Radio;
  if (t.includes("ball") || t.includes("gala")) return Sparkles;
  return Ticket;
};

const statusPill = (status) => {
  if (status === "coming_soon") return { label: "Coming Soon", cls: "border-bdu-gold/50 text-bdu-gold" };
  if (status === "upcoming") return { label: "Upcoming", cls: "border-emerald-500/50 text-emerald-400" };
  if (status === "past") return { label: "Past Event", cls: "border-bdu-cream/30 text-bdu-cream/50" };
  return { label: status, cls: "border-bdu-cream/30 text-bdu-cream/60" };
};

function ComingSoonPanel() {
  return (
    <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0" style={{
        background: "radial-gradient(circle at center, rgba(212,175,55,0.15) 0%, rgba(0,0,0,0) 70%)"
      }} />
      <div className="relative z-10 text-center px-6" data-testid="events-coming-soon-block">
        <div
          className="font-serif text-bdu-gold tracking-tight leading-none"
          style={{
            fontSize: "clamp(3rem, 8vw, 6.5rem)",
            animation: "bduBlink 1.6s ease-in-out infinite",
            textShadow: "0 0 24px rgba(212,175,55,0.35)",
          }}
        >
          COMING
        </div>
        <div
          className="font-serif text-bdu-gold tracking-tight leading-none mt-2"
          style={{
            fontSize: "clamp(3rem, 8vw, 6.5rem)",
            animation: "bduBlink 1.6s ease-in-out infinite",
            animationDelay: "0.3s",
            textShadow: "0 0 24px rgba(212,175,55,0.35)",
          }}
        >
          SOON
        </div>
        <div className="mt-6 text-xs uppercase tracking-[0.4em] text-bdu-cream/60">
          Stay Tuned
        </div>
      </div>
    </div>
  );
}

function EventImagePanel({ imageUrl, imageFit, status }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [imageUrl]);

  if (!imageUrl || broken) {
    return <ComingSoonPanel />;
  }
  const isComingSoon = status === "coming_soon";
  return (
    <>
      {/* hidden probe img detects 404s and switches to fallback */}
      <img
        src={imageUrl}
        alt=""
        onError={() => setBroken(true)}
        style={{ display: "none" }}
      />
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${imageUrl})`,
          backgroundSize: imageFit === "contain" ? "contain" : "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          filter: imageFit === "contain" ? "none" : "saturate(0.9) contrast(1.05)",
        }}
      />
      {imageFit !== "contain" && (
        <div className="absolute inset-0 bg-gradient-to-t from-bdu-bg via-black/40 to-transparent" />
      )}
      {/* Blinking COMING SOON banner overlay when event is coming_soon */}
      {isComingSoon && (
        <div
          className="absolute top-0 left-0 right-0 z-10 flex items-center justify-center py-3 backdrop-blur-sm border-b border-bdu-gold/40"
          style={{
            background: "linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(212,175,55,0.15) 50%, rgba(0,0,0,0.85) 100%)",
            animation: "bduBlink 1.6s ease-in-out infinite",
          }}
          data-testid="events-coming-soon-banner"
        >
          <span
            className="font-serif text-bdu-gold tracking-[0.35em] uppercase"
            style={{
              fontSize: "clamp(1.25rem, 2.5vw, 2rem)",
              textShadow: "0 0 20px rgba(212,175,55,0.6)",
            }}
          >
            ★ Coming Soon ★
          </span>
        </div>
      )}
    </>
  );
}

export default function Events() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketEvent, setTicketEvent] = useState(null);

  useEffect(() => {
    api.get("/events").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="grain min-h-screen">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-16" data-testid="events-hero">
        <div className="label-eyebrow">Fundraising Events</div>
        <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl text-bdu-cream tracking-tight max-w-4xl">
          The nights, the broadcasts, the moments <span className="italic text-bdu-gold">that build the fund.</span>
        </h1>
        <p className="mt-6 text-bdu-cream/70 max-w-2xl leading-relaxed">
          Every event is a direct engine for the Trillion Dollar Spending Power Fund — raising the $50M we need
          in 24 months to build our own manufacturing, grocery, banking, and healthcare infrastructure.
        </p>
      </section>

      {/* EVENTS LIST */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {loading ? (
          <div className="text-bdu-cream/50 py-16 text-center">Loading events...</div>
        ) : items.length === 0 ? (
          <div className="bdu-card text-center py-16" data-testid="events-empty">
            <div className="font-serif text-3xl text-bdu-cream">Events coming soon.</div>
            <p className="mt-3 text-bdu-cream/60">Our team is finalizing the calendar. Check back — or join the fund to be notified first.</p>
            <Link to="/register" className="btn-gold mt-8 inline-flex">Join the Fund</Link>
          </div>
        ) : (
          <div className="space-y-8" data-testid="events-list">
            {items.map((ev, i) => {
              const Icon = iconFor(ev.title);
              const pill = statusPill(ev.status);
              const flip = i % 2 === 1;
              return (
                <article
                  key={ev.id}
                  className="border border-bdu-gold/10 bg-bdu-surface overflow-hidden animate-fade-up"
                  style={{ animationDelay: `${i * 100}ms` }}
                  data-testid={`event-card-${ev.id}`}
                >
                  <div className="grid lg:grid-cols-12 gap-0">
                    {/* Image */}
                    <div
                      className={`lg:col-span-5 relative ${ev.image_fit === "contain" ? "min-h-[520px] lg:min-h-[720px]" : "min-h-[280px] lg:min-h-[420px]"} ${flip ? "lg:order-2" : ""}`}
                      style={{ backgroundColor: "#0A0A0A" }}
                    >
                      <EventImagePanel imageUrl={ev.image_url} imageFit={ev.image_fit} status={ev.status} />
                      {/* Small status pill only when NOT coming_soon (coming_soon renders the big blinking banner) */}
                      {ev.status !== "coming_soon" && (
                        <div className="absolute top-6 left-6 z-20">
                          <span className={`text-[0.6rem] uppercase tracking-[0.24em] px-3 py-1.5 border ${pill.cls} bg-black/70 backdrop-blur-sm`}>
                            {pill.label}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="lg:col-span-7 p-8 lg:p-12 flex flex-col justify-center">
                      <div className="flex items-center gap-3">
                        <Icon size={22} strokeWidth={1.25} className="text-bdu-gold" />
                        <span className="label-eyebrow">{ev.presented_by || "Black Dollars United"}</span>
                      </div>
                      <h2 className="mt-5 font-serif text-4xl sm:text-5xl text-bdu-cream leading-[0.95] tracking-tight">
                        {ev.title}
                      </h2>
                      {ev.subtitle && (
                        <div className="mt-3 font-serif italic text-xl text-bdu-gold/90">{ev.subtitle}</div>
                      )}

                      <div className="mt-6 flex flex-wrap gap-6 text-sm text-bdu-cream/60">
                        {ev.event_date && (
                          <div className="flex items-center gap-2"><CalendarDays size={14} /> {ev.event_date}</div>
                        )}
                        {ev.location && (
                          <div className="flex items-center gap-2"><MapPin size={14} /> {ev.location}</div>
                        )}
                      </div>

                      <p className="mt-6 text-bdu-cream/75 leading-loose max-w-2xl">
                        {ev.description}
                      </p>

                      {ev.countdown_to && (
                        <Countdown target={ev.countdown_to} testId={`event-countdown-${ev.id}`} />
                      )}

                      <div className="mt-8 flex flex-wrap gap-3">
                        {ev.title && ev.title.toLowerCase().includes("black royal unity ball") && (
                          <button
                            type="button"
                            onClick={() => setTicketEvent(ev)}
                            className="btn-gold"
                            data-testid={`event-buy-tickets-${ev.id}`}
                          >
                            Buy Tickets
                          </button>
                        )}
                        {ev.cta_label && ev.cta_url ? (
                          ev.cta_url.startsWith("/") ? (
                            <Link to={ev.cta_url} className="btn-ghost" data-testid={`event-cta-${ev.id}`}>
                              {ev.cta_label}
                            </Link>
                          ) : (
                            <a href={ev.cta_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`event-cta-${ev.id}`}>
                              {ev.cta_label}
                            </a>
                          )
                        ) : null}
                        {ev.flyer_url && (
                          <a href={ev.flyer_url} target="_blank" rel="noreferrer" className="btn-ghost" data-testid={`event-flyer-${ev.id}`}>
                            View Full Flyer
                          </a>
                        )}
                        <Link to="/pledge" className="btn-ghost">Pledge to the Fund</Link>
                      </div>
                    </div>
                  </div>

                  {/* Past highlights (photos + testimonials + total raised) */}
                  <div className="px-8 lg:px-12 pb-10">
                    <EventOrganizer event={ev} />
                    <EventHighlights event={ev} />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {/* Buy Tickets modal — controlled from Ball card's Buy Tickets button */}
      <BuyTicketsModal
        open={!!ticketEvent}
        onClose={() => setTicketEvent(null)}
        event={ticketEvent}
      />
    </div>
  );
}
